import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App.jsx';
import SetPasswordPage from './components/SetPasswordPage.jsx';
import SignInPage from './components/SignInPage.jsx';
import 'leaflet/dist/leaflet.css';
import './styles.css';

// A tab that outlives a deploy holds references to old content-hashed chunk
// names; the first lazy import after that 404s (and the SPA rewrite turns it
// into a text/html MIME error). The fix is simply loading the new build —
// reload once automatically instead of showing the crash screen. The
// timestamp guard prevents a reload loop if the network itself is down.
const isStaleChunkError = (error) =>
  /dynamically imported module|Importing a module script failed|error loading|Failed to fetch/i
    .test(error?.message ?? '');

const reloadOnceForNewBuild = () => {
  const KEY = 'weinberge_chunk_reload_at';
  const last = Number(sessionStorage.getItem(KEY) || 0);
  if (Date.now() - last < 30000) return false;
  sessionStorage.setItem(KEY, String(Date.now()));
  window.location.reload();
  return true;
};

// Vite fires this on failed dynamic imports/preloads.
window.addEventListener('vite:preloadError', (event) => {
  if (reloadOnceForNewBuild()) event.preventDefault();
});

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, report: null, copied: false };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch(error, info) {
    // Stale-deploy chunk failure: recover by loading the new build. Only
    // fall through to the crash screen if a reload was just attempted.
    if (isStaleChunkError(error) && reloadOnceForNewBuild()) return;
    console.error('[ErrorBoundary]', error, info.componentStack);
    console.error('[ErrorBoundary] App state:', JSON.stringify(window.__APP_DEBUG ?? 'not yet initialized', null, 2));
    // Persist a crash report so intermittent errors survive the reload:
    // recover it later with localStorage.getItem('weinberge_last_crash').
    const report = [
      `time: ${new Date().toISOString()}`,
      `error: ${error?.message ?? String(error)}`,
      `stack: ${(error?.stack ?? '').split('\n').slice(0, 8).join('\n')}`,
      `component stack:${(info?.componentStack ?? '').split('\n').slice(0, 12).join('\n')}`,
      `app state: ${JSON.stringify(window.__APP_DEBUG ?? null)}`,
    ].join('\n\n');
    try { localStorage.setItem('weinberge_last_crash', report); } catch { /* storage full/blocked */ }
    this.setState({ report });
  }
  render() {
    if (this.state.error) {
      return (
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          height: '100vh', padding: '2rem', textAlign: 'center',
          fontFamily: 'Inter, sans-serif', color: '#333', background: '#ffffff'
        }}>
          <h1 style={{ fontSize: '1.8rem', fontWeight: 600, color: '#4A235A', marginBottom: '0.8rem' }}>
            German Wine Map
          </h1>
          <p style={{ fontSize: '0.85rem', color: '#6B7280', maxWidth: '360px', lineHeight: 1.6, marginBottom: '0.4rem' }}>
            A vineyard study tool built as part of my German Wine Scholar studies.
          </p>
          <p style={{ fontSize: '0.75rem', color: '#9CA3AF', maxWidth: '360px', lineHeight: 1.5, marginBottom: '2rem' }}>
            This is a personal educational project and is not intended for commercial use.
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: '0.7rem 2rem', fontSize: '0.9rem', fontWeight: 600,
              cursor: 'pointer', border: 'none', borderRadius: '12px',
              background: '#4A235A', color: '#fff',
              boxShadow: '2px 2px 8px rgba(0,0,0,0.1)',
              transition: 'background 0.15s'
            }}
            onMouseEnter={e => e.target.style.background = '#3b1a47'}
            onMouseLeave={e => e.target.style.background = '#4A235A'}
          >
            Let's Go
          </button>
          {this.state.report && (
            <button
              onClick={() => {
                navigator.clipboard?.writeText(this.state.report)
                  .then(() => this.setState({ copied: true }))
                  .catch(() => {});
              }}
              style={{
                marginTop: '1rem', padding: '0.4rem 1rem', fontSize: '0.72rem',
                cursor: 'pointer', border: '1px solid #d1d5db', borderRadius: '8px',
                background: 'transparent', color: '#9CA3AF'
              }}
            >
              {this.state.copied ? 'Copied' : 'Copy error details'}
            </button>
          )}
        </div>
      );
    }
    return this.props.children;
  }
}

const path = window.location.pathname;
const hash = window.location.hash;

// Supabase redirects recovery/invite to root with hash tokens — require access_token present
const isRecoveryAtRoot = path === '/' && hash.includes('access_token') && (hash.includes('type=recovery') || hash.includes('type=invite'));

// Normalize a trailing slash so /auth/sign-in/ works like /auth/sign-in
const cleanPath = path.replace(/\/+$/, '') || '/';

let page;
if (cleanPath === '/auth/set-password' || isRecoveryAtRoot) {
  page = <SetPasswordPage />;
} else if (cleanPath === '/auth/sign-in') {
  page = <SignInPage />;
} else if (cleanPath === '/auth' || cleanPath.startsWith('/auth/')) {
  // No other /auth/* URLs exist — don't silently serve the map at them
  window.location.replace('/auth/sign-in');
  page = null;
} else {
  page = <App />;
}

// The index.html load veil is for the map; only App dismisses it, so
// drop it immediately on any other page (auth) or it would sit forever.
if (page === null || cleanPath.startsWith('/auth')) {
  document.getElementById('load-veil')?.remove();
}

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <ErrorBoundary>
      {page}
    </ErrorBoundary>
  </React.StrictMode>
);
